import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { VEHICLES, getVehicleDisplayName } from '../data/bookingConfig';
import {
  getPopularRouteSlides,
  quoteRouteVehicleFare,
  tollsAndParkingIncluded,
} from '../data/popularRoutes';
import { prefetchRouteMetrics, readCachedRouteMetrics } from '../services/routeMetrics';
import '../styles/FeaturedRoutePromotion.css';

const DEFAULT_VEHICLE = 'Sedan';

const formatInr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;

const shortenDuration = (text) => String(text || '')
  .replace(/hours?/gi, 'hr')
  .replace(/mins?/gi, 'min')
  .replace(/\s+/g, ' ')
  .trim();

const seatLabel = (seats) => String(seats || '').replace(/Seats/i, 'Seater');

const RouteCard = ({ route, vehicleType, metrics, distancePending, onSelectVehicle, onEstimate }) => {
  const distanceKm = metrics?.distanceKm || null;
  const selected = VEHICLES.find((item) => item.type === vehicleType) || VEHICLES[0];
  const fare = quoteRouteVehicleFare(route.path, selected.type, distanceKm);
  const included = tollsAndParkingIncluded(route.origin, route.destination, route.path);
  const displayName = getVehicleDisplayName(selected);
  const duration = metrics?.durationText ? `Approx ${shortenDuration(metrics.durationText)}` : null;
  const distanceLabel = distanceKm ? `${Math.round(distanceKm)} km` : null;
  const meta = [distanceLabel, duration].filter(Boolean).join(' • ')
    || (distancePending ? 'Checking live route distance' : 'Live route distance unavailable');

  return (
    <article className="pr-card">
      <p className="pr-trip-badge">{route.tripTypeLabel}</p>
      <h3 className="pr-route-title">
        <span className="pr-place-name">{route.origin}</span>
        <span className="pr-place-divider" aria-hidden="true">→</span>
        <span className="pr-place-name">{route.destination}</span>
      </h3>
      <p className="pr-route-meta">{meta || 'Distance and time from the live route'}</p>

      <div className="pr-vehicles" role="group" aria-label={`${route.origin} to ${route.destination} vehicles`}>
        {VEHICLES.map((vehicle) => {
          const active = vehicle.type === selected.type;
          const optionFare = quoteRouteVehicleFare(route.path, vehicle.type, distanceKm);
          const label = getVehicleDisplayName(vehicle);
          return (
            <button
              key={vehicle.type}
              type="button"
              className={`pr-vehicle${active ? ' is-selected' : ''}`}
              aria-pressed={active}
              onClick={() => onSelectVehicle(route.id, vehicle.type)}
            >
              {active && <span className="pr-vehicle-check" aria-hidden="true">✓</span>}
              <img src={vehicle.image} alt="" className="pr-vehicle-img" />
              <span className="pr-vehicle-name">{label}</span>
              <span className="pr-vehicle-fare">{optionFare == null ? '' : formatInr(optionFare)}</span>
            </button>
          );
        })}
      </div>

      <p className="pr-spec">
        {displayName}
        {' • '}
        {seatLabel(selected.seats)}
        {' • AC'}
      </p>
      <p className={`pr-tolls${included ? ' is-included' : ' is-extra'}`}>
        <span aria-hidden="true">{included ? '✓' : '!'}</span>
        {included ? 'Tolls + Parking included' : 'Toll & Parking Charges Additional'}
      </p>

      <button
        type="button"
        className="pr-estimate"
        onClick={() => onEstimate(route, selected.type)}
      >
        Get Fare Estimate
      </button>
      <Link className="pr-details" to={route.path}>
        View Route Details
      </Link>
    </article>
  );
};

const FeaturedRoutePromotion = ({
  origin,
  destination,
  onGetFareEstimate,
}) => {
  const model = useMemo(
    () => getPopularRouteSlides({ origin, destination }),
    [origin, destination],
  );
  const slides = model.slides;
  const trackRef = useRef(null);
  const pausedRef = useRef(false);
  const resumeTimer = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedByRoute, setSelectedByRoute] = useState({});
  const [metricsByRoute, setMetricsByRoute] = useState({});
  const [distancePending, setDistancePending] = useState(true);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReducedMotion(media.matches);
    apply();
    media.addEventListener?.('change', apply);
    return () => media.removeEventListener?.('change', apply);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
    setSelectedByRoute({});
    setMetricsByRoute({});
  }, [model.heading]);

  const scrollToIndex = useCallback((index) => {
    const track = trackRef.current;
    const slide = track?.children?.[index];
    if (!track || !slide) return;
    const left = slide.offsetLeft - (track.children[0]?.offsetLeft || 0);
    track.scrollTo({ left, behavior: reducedMotion ? 'auto' : 'smooth' });
    setActiveIndex(index);
  }, [reducedMotion]);

  const pause = useCallback((resumeMs = 5000) => {
    pausedRef.current = true;
    window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => {
      pausedRef.current = false;
    }, resumeMs);
  }, []);

  useEffect(() => () => window.clearTimeout(resumeTimer.current), []);

  useEffect(() => {
    if (reducedMotion || slides.length < 2) return undefined;
    const id = window.setInterval(() => {
      if (pausedRef.current) return;
      setActiveIndex((current) => {
        const next = (current + 1) % slides.length;
        const track = trackRef.current;
        const slide = track?.children?.[next];
        if (track && slide) {
          const left = slide.offsetLeft - (track.children[0]?.offsetLeft || 0);
          track.scrollTo({ left, behavior: 'smooth' });
        }
        return next;
      });
    }, 5000);
    return () => window.clearInterval(id);
  }, [reducedMotion, slides.length, model.heading]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = 0;
    let readyAttempts = 0;
    let missAttempts = 0;
    const pairs = slides.map((route) => [route.origin, route.destination]);

    const applyCached = () => {
      setMetricsByRoute((current) => {
        const next = { ...current };
        let changed = false;
        slides.forEach((route) => {
          if (next[route.id]) return;
          const metrics = readCachedRouteMetrics(route.origin, route.destination);
          if (!metrics) return;
          next[route.id] = metrics;
          changed = true;
        });
        return changed ? next : current;
      });
    };

    const load = async () => {
      const ready = await prefetchRouteMetrics(pairs);
      if (cancelled) return;
      if (!ready) {
        if (readyAttempts < 10) {
          readyAttempts += 1;
          retryTimer = window.setTimeout(load, 400);
        } else {
          setDistancePending(false);
        }
        return;
      }
      applyCached();
      const missing = slides.some((route) => !readCachedRouteMetrics(route.origin, route.destination));
      if (missing && missAttempts < 1) {
        missAttempts += 1;
        retryTimer = window.setTimeout(load, 600);
        return;
      }
      setDistancePending(false);
    };

    setDistancePending(true);
    if (window.google?.maps || window.googleMapsReady) load();
    else window.addEventListener('google-maps-ready', load, { once: true });

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      window.removeEventListener('google-maps-ready', load);
    };
  }, [slides]);

  const updateActiveFromScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const left = track.scrollLeft;
    let nearest = 0;
    let best = Infinity;
    Array.from(track.children).forEach((slide, index) => {
      const delta = Math.abs((slide.offsetLeft - (track.children[0]?.offsetLeft || 0)) - left);
      if (delta < best) {
        best = delta;
        nearest = index;
      }
    });
    setActiveIndex(nearest);
  }, []);

  const selectVehicle = (routeId, vehicleType) => {
    pause(5000);
    setSelectedByRoute((current) => ({ ...current, [routeId]: vehicleType }));
  };

  const estimate = (route, vehicleType) => {
    pause(5000);
    if (onGetFareEstimate) {
      onGetFareEstimate({
        origin: route.origin,
        destination: route.destination,
        vehicleType,
        path: route.path,
      });
    }
  };

  const onTrackKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      pause();
      scrollToIndex(Math.min(slides.length - 1, activeIndex + 1));
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      pause();
      scrollToIndex(Math.max(0, activeIndex - 1));
    }
  };

  if (!slides.length) return null;

  return (
    <section className="popular-routes" aria-label={model.badge}>
      <div className="pr-wrap">
        <header className="pr-header">
          <p className="pr-badge">🔥 {model.badge}</p>
        </header>

        <div className="pr-carousel">
          <div
            className="pr-track"
            ref={trackRef}
            role="region"
            aria-roledescription="carousel"
            aria-label={model.heading}
            tabIndex={0}
            onKeyDown={onTrackKeyDown}
            onScroll={updateActiveFromScroll}
            onPointerDown={() => pause(5000)}
            onPointerUp={() => pause(5000)}
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
          >
            {slides.map((route, index) => (
              <div
                key={route.id}
                className="pr-slide"
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${slides.length}`}
              >
                <RouteCard
                  route={route}
                  vehicleType={selectedByRoute[route.id] || DEFAULT_VEHICLE}
                  metrics={metricsByRoute[route.id]}
                  distancePending={distancePending && !metricsByRoute[route.id]}
                  onSelectVehicle={selectVehicle}
                  onEstimate={estimate}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="pr-dots" role="tablist" aria-label="Popular route slides">
          {slides.map((route, index) => (
            <button
              key={route.id}
              type="button"
              role="tab"
              className={`pr-dot${index === activeIndex ? ' is-active' : ''}`}
              aria-selected={index === activeIndex}
              aria-label={`Show ${route.origin} to ${route.destination}`}
              onClick={() => {
                pause();
                scrollToIndex(index);
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedRoutePromotion;
